import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTriangleExclamation,
    faCircleCheck, faCircleXmark, faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import LaboratoryLogo from '../Laboratory/LaboratoryLogo';
import BarcodeComponent from '../BarcodeComponent';
import { transliterateToArabic } from '../../utils/transliterateArabic';

// Shared product-listing columns (Table / EntityTable column definitions), so every product
// list — the Products page, a laboratory's products, the product page's Similaires / Même DCI —
// renders the same cells. Each builder returns a plain column object; callers spread it and add
// what's specific to their screen (a `filterable`, a different `Header`...), e.g.
//     { ...productPriceColumn({ t, canEdit, onEditPrice }), filterable: { ... } }

export const formatAmount = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

export const productIdColumn = () => ({
    Header: '#',
    accessor: 'id',
    sortable: true,
    className: 'text-center',
    Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
});

// showLaboratory: false on a laboratory's own page, where "Par <labo>" would repeat on every row.
// getLink(row): the name's link (default: the in-app product page; the public catalog passes
// its own). linkLaboratory: false where the in-app laboratory page isn't reachable (public).
export const productNameColumn = ({ t, isArabic = false, showLaboratory = true, getLink = (row) => `/products/${row.id}`, linkLaboratory = true }) => ({
    Header: t('Product'),
    accessor: 'name',
    sortable: true,
    minWidth: '420px',
    skeleton: 'media', // loading placeholder: thumbnail + two lines
    Cell: (row) => (
        <div className="d-flex align-items-start gap-3" style={{ paddingBlock: 2 }}>
            <img
                src={row.images?.length > 0 ? row.images[0].url : '/images/no_product.png'}
                alt={row.name}
                style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            />
            <div style={{ minWidth: 0, maxWidth: 340 }}>
                <NavLink
                    to={getLink(row)}
                    className="cell-name"
                    dir={isArabic ? 'rtl' : undefined}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={row.name}
                >
                    {isArabic ? (row.nameAr || transliterateToArabic(row.name)) : row.name}
                </NavLink>
                {(row.presentation || row.dosage) ? (
                    <span
                        className="cell-sub"
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 4, color: '#111827' }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content={[row.presentation, row.dosage].filter(Boolean).join(' · ')}
                    >
                        {row.presentation}{row.presentation && row.dosage ? ' · ' : ''}{row.dosage}
                    </span>
                ) : (
                    <span className="cell-sub cell-muted" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>Pas de présentation</span>
                )}
                {showLaboratory && row.laboratory?.name && (linkLaboratory ? (
                    <NavLink
                        to={`/suppliers/${row.laboratory.id}`}
                        className="cell-sub"
                        style={{ textDecoration: 'none', color: '#374151', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}
                        onClick={e => e.stopPropagation()}
                    >
                        Par <LaboratoryLogo id={row.laboratory.id} name={row.laboratory.name} size={16} />
                        {row.laboratory.name}
                    </NavLink>
                ) : (
                    <span className="cell-sub" style={{ color: '#374151', display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                        Par <LaboratoryLogo id={row.laboratory.id} name={row.laboratory.name} size={16} />
                        {row.laboratory.name}
                    </span>
                ))}
            </div>
        </div>
    ),
});

// onEditPrice(row): when given, the price is click-to-edit (dashed underline).
// referencePrice: when given, shows each row's price gap with it underneath (Similaires).
export const productPriceColumn = ({ t, onEditPrice, referencePrice }) => ({
    Header: t('Price'),
    accessor: 'publicPrice',
    className: 'text-end',
    activeColumn: true,
    sortable: true,
    Cell: (row) => {
        const diff = referencePrice != null && row.publicPrice != null
            ? Number(row.publicPrice) - Number(referencePrice)
            : null;
        return (
            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 4 }}>
                    {onEditPrice ? (
                        <span
                            className="cell-price-editable"
                            style={{ fontSize: 13.5, color: '#000', cursor: 'pointer', borderBottom: '1px dashed #ced4da', fontWeight: 600 }}
                            data-tooltip-id="tooltip"
                            data-tooltip-content="Modifier le prix"
                            onClick={(e) => { e.stopPropagation(); onEditPrice(row); }}
                        >
                            {formatAmount(row.publicPrice)}
                        </span>
                    ) : (
                        <span style={{ fontSize: 13.5, color: '#111827', fontWeight: 600 }}>
                            {formatAmount(row.publicPrice)}
                        </span>
                    )}
                    <span style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
                </div>
                {diff != null && Math.abs(diff) >= 0.005 && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: diff < 0 ? '#059669' : '#dc2626' }}>
                        {diff < 0 ? '−' : '+'}{formatAmount(Math.abs(diff))} dhs
                    </span>
                )}
                {diff != null && Math.abs(diff) < 0.005 && (
                    <span style={{ fontSize: 11, fontWeight: 500, color: '#9ca3af' }}>Même prix</span>
                )}
            </div>
        );
    },
});

export const productStockColumn = ({ t, alertThreshold = 10 }) => ({
    Header: t('Stock'),
    accessor: 'totalStock',
    sortField: 'stockCount',
    sortable: true,
    className: 'text-end',
    Cell: (row) => {
        const qty = row.totalStock;
        if (qty <= 0 && !row.hasStockHistory) {
            return <span className="cell-muted">—</span>;
        }
        // right-aligned quantity with a grey unit (same look as the price's "dhs"), and a small
        // level bar underneath: full at twice the alert threshold (product's own, else the
        // pharmacy's), amber at or under the threshold, empty track when out of stock
        const threshold = row.minimumStock > 0 ? row.minimumStock : alertThreshold;
        const fill = Math.min(1, qty / (threshold * 2));
        const color = qty <= 0 ? '#ef4444' : qty <= threshold ? '#f59e0b' : '#22c55e';
        const state = qty <= 0 ? 'Rupture' : qty <= threshold ? 'Stock faible' : 'En stock';
        return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: qty <= 0 ? '#9ca3af' : '#111827', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {qty} <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>u.</span>
                </span>
                <span
                    data-tooltip-id="tooltip"
                    data-tooltip-content={`${state} · seuil d'alerte : ${threshold}`}
                    style={{
                        display: 'block', width: 44, height: 4, borderRadius: 999, overflow: 'hidden',
                        background: qty <= 0 ? '#fee2e2' : '#eef1f5',
                    }}
                >
                    <span style={{ display: 'block', height: '100%', width: `${fill * 100}%`, background: color, borderRadius: 999 }} />
                </span>
                {row.expiredBatchCount > 0 && (
                    <span
                        style={{ fontSize: 11, fontWeight: 600, color: '#f97316', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content={`${row.expiredBatchCount} lot${row.expiredBatchCount > 1 ? 's' : ''} périmé${row.expiredBatchCount > 1 ? 's' : ''}`}
                    >
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: 10 }} />
                        {row.expiredBatchCount} périmé{row.expiredBatchCount > 1 ? 's' : ''}
                    </span>
                )}
            </span>
        );
    },
});

export const productStatusColumn = ({ t }) => ({
    Header: t('Status'),
    accessor: 'status',
    className: 'text-center',
    maxWidth: '70px',
    Cell: (row) => {
        const marketed = row.status === 'MARKETED';
        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <FontAwesomeIcon
                    icon={marketed ? faCircleCheck : faCircleXmark}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={marketed ? t('Marketed') : t('Not marketed')}
                    style={{ color: marketed ? '#16a34a' : '#9ca3af', fontSize: 16 }}
                />
            </div>
        );
    },
});

export const productRefundableColumn = ({ t }) => ({
    Header: (
        <span data-tooltip-id="tooltip" data-tooltip-content={t('Refundable')} style={{ cursor: 'default' }}>
            <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: 13, color: '#6b7280' }} />
        </span>
    ),
    label: 'Remboursable',
    accessor: 'refundable',
    className: 'text-center',
    // Same flex wrapper as productStatusColumn — a bare inline <svg> sits on the text baseline,
    // a couple of pixels off from the status icon next to it.
    Cell: (row) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            {row.refundable === true ? (
                <FontAwesomeIcon icon={faCircleCheck} data-tooltip-id="tooltip" data-tooltip-content="Remboursable" style={{ color: '#16a34a', fontSize: 16 }} />
            ) : row.refundable === false ? (
                <FontAwesomeIcon icon={faCircleXmark} data-tooltip-id="tooltip" data-tooltip-content="Non remboursable" style={{ color: '#9ca3af', fontSize: 16 }} />
            ) : (
                <span className="cell-muted">—</span>
            )}
        </div>
    ),
});

export const productBarcodeColumn = ({ t }) => ({
    Header: t('Barcode'),
    accessor: 'barcode',
    className: 'text-center',
    Cell: (row) => {
        if (!row.barcode || row.barcode === 'N/A') {
            return <span className="cell-muted fst-italic">{t('None')}</span>;
        }
        return (
            <div style={{ width: 140, margin: '0 auto' }}>
                <BarcodeComponent format="EAN13" value={row.barcode} height={22} fontSize={11} />
            </div>
        );
    },
});
